import { useNavigate } from 'react-router-dom';

import { enqueueSnackbar } from 'notistack';
import { useMutation } from '@tanstack/react-query';

import {
    Box,
    Container,
    IconButton,
    Loading,
    LoadingButton,
    Stack,
    TextField,
    Typography,
} from '@thirdspacelearning/library';
import { generateLocation } from '@tsl-frontend/tsl-utilities';

import {
    FeelingMeh,
    ItWasGood,
    LoveIt,
    Neutral,
    NotHappy,
} from '@assets/feedback';

import { ChangeEvent, useEffect, useState } from 'react';
import { appLoadedSelector } from '@store/initialisation';
import { useSelector } from 'react-redux';
import { lastUncompletedSessionSelector } from '@store/session';
import { submitPostSessionFeedback } from '@api';
import { RoutePaths } from '@routes';

const sx = {
    paper: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        borderRadius: '70px',
        padding: 5,
        gap: 4,
    },
    topic: {
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: 'inherit',
    },
    faces: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '15px',
        border: '1px solid',
        borderColor: 'grey.400',
        borderRadius: 1,
        flexDirection: {
            xs: 'column',
            sm: 'row',
        },
        gap: {
            xs: 2,
            sm: 0,
        },
    },
    smiliesContent: {
        direction: 'row',
        height: '100%',
        justifyContent: 'space-between',
        gap: {
            sm: 0,
            md: 2,
        },
    },
    pendingContent: {
        gap: '32px',
        alignItems: 'center',
        justifyContent: 'center',
        height: '330px',
    },
};

const enum Smilies {
    NOT_HAPPY = 1,
    FEELING_MEH,
    NUTREAL,
    IT_WAS_GOOD,
    LOVE_IT,
}

const smilies = [
    {
        value: Smilies.NOT_HAPPY,
        icon: (filled: boolean) => <NotHappy filled={filled} />,
    },
    {
        value: Smilies.FEELING_MEH,
        icon: (filled: boolean) => <FeelingMeh filled={filled} />,
    },
    {
        value: Smilies.NUTREAL,
        icon: (filled: boolean) => <Neutral filled={filled} />,
    },
    {
        value: Smilies.IT_WAS_GOOD,
        icon: (filled: boolean) => <ItWasGood filled={filled} />,
    },
    {
        value: Smilies.LOVE_IT,
        icon: (filled: boolean) => <LoveIt filled={filled} />,
    },
];

const SessionFeedbackSurvey = () => {
    const navigate = useNavigate();

    const pending = useSelector(appLoadedSelector);
    const lastSession = useSelector(lastUncompletedSessionSelector);

    const [sessionRate, setSessionRate] = useState<Smilies | undefined>();

    const [comment, setComment] = useState('');

    const [showFeedbackForm, swrShowFeedbackForm] = useState(false);

    const { mutate: submitFeedback, isPending } = useMutation({
        mutationFn: () =>
            submitPostSessionFeedback(
                comment,
                Number(sessionRate),
                lastSession?.session?.id,
            ),
        onSuccess: () => {
            navigate(
                generateLocation(
                    RoutePaths.DASHBOARD,
                    {},
                    {
                        sessionDone: true,
                    },
                ),
            );
        },
        onError: (error: Error) => {
            enqueueSnackbar(error.message, {
                variant: 'sxSnackbar',
                severity: 'error',
                testId: 'save-post-session-feedback-error',
            });
        },
    });

    useEffect(() => {
        if (!pending) {
            swrShowFeedbackForm(true);
        }
    }, [pending]);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        setComment(event.target.value);
    };

    if (pending) {
        return <Loading />;
    }

    if (!lastSession?.session?.['id']) {
        navigate(RoutePaths.DASHBOARD);
    }

    return (
        <Container maxWidth="md">
            <Stack justifyContent="center" gap={2}>
                <Typography variant="h4" color="textPrimary">
                    <Box fontWeight="bold" component="b">
                        One last thing, a few questions to hear how your session
                        went.
                    </Box>
                </Typography>

                <Box sx={sx.paper}>
                    <Stack
                        sx={{
                            gap: 1,
                            ...(showFeedbackForm && {
                                opacity: 1,
                                height: '100%',
                                transition: 'all 2s',
                            }),
                            ...(!showFeedbackForm && {
                                opacity: 0,
                                height: 0,
                            }),
                        }}
                    >
                        <Stack height="inherit" gap={3}>
                            <Box sx={sx.topic}>
                                <Typography
                                    variant="h5"
                                    color="textPrimary"
                                    fontWeight="600"
                                >
                                    How was your session today?
                                </Typography>

                                <Stack gap={1}>
                                    <Box sx={sx.faces}>
                                        <Typography
                                            variant="h6"
                                            color="textPrimary"
                                            fontWeight="500"
                                            display="flex"
                                            alignItems="center"
                                        >
                                            Today’s session was enjoyable
                                        </Typography>

                                        <Stack>
                                            <Stack
                                                direction="row"
                                                height="100%"
                                                justifyContent="space-between"
                                                fontSize="14px"
                                            >
                                                <Box component="span">
                                                    Not at all
                                                </Box>
                                                <Box component="span">
                                                    Neutral
                                                </Box>
                                                <Box component="span">
                                                    Loved it!
                                                </Box>
                                            </Stack>

                                            <Stack
                                                direction="row"
                                                sx={sx.smiliesContent}
                                            >
                                                {smilies.map(
                                                    ({ value, icon }) => (
                                                        <IconButton
                                                            key={value}
                                                            size="small"
                                                            disabled={isPending}
                                                            onClick={() =>
                                                                setSessionRate(
                                                                    value,
                                                                )
                                                            }
                                                        >
                                                            {icon(
                                                                value ===
                                                                    sessionRate,
                                                            )}
                                                        </IconButton>
                                                    ),
                                                )}
                                            </Stack>
                                        </Stack>
                                    </Box>
                                </Stack>
                            </Box>

                            <Box sx={sx.topic}>
                                <Stack justifyContent="space-between">
                                    <Typography
                                        variant="h5"
                                        color="textPrimary"
                                        fontWeight="600"
                                    >
                                        Is there anything you would like to tell
                                        us?
                                    </Typography>
                                </Stack>

                                <TextField
                                    value={comment}
                                    onChange={onChange}
                                    disabled={isPending}
                                    autoComplete="off"
                                    autoCorrect="off"
                                    placeholder="I want to say..."
                                />
                            </Box>
                        </Stack>

                        <LoadingButton
                            color="primary"
                            variant="contained"
                            size="medium"
                            children={'Submit My Answers'}
                            loading={isPending}
                            disabled={!sessionRate}
                            onClick={() => submitFeedback()}
                            sx={{ ml: 'auto' }}
                        />
                    </Stack>
                </Box>
            </Stack>
        </Container>
    );
};

export default SessionFeedbackSurvey;
